/* Minibar.jsx — Oda bazlı dolum takibi (yeni arayüz).
 * 340 oda (5 kat × 68): 101-168, 201-268, 301-368, 401-468, 501-568.
 * Tarih seçici + sol tarafta oda arama. Aktif oda kartı: 9 ürün adet + oda toplamı.
 * Oda durumları: Boş (beyaz), Dolu (yeşil), Çıkış (turuncu). DND ayrı (kırmızı rozet);
 *   DND'de ürün girilmez/sıfırlanır.
 * Kat sekmeleri 1-5: kart ızgarası, durum renkli.
 */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Loader2, Save, Coffee, AlertTriangle, Search, BellOff, X, ClipboardList, BarChart3, CalendarRange } from 'lucide-react';
import { formatTRNumber } from '../lib/utils';
import MinibarReports from '../components/MinibarReports';
import MinibarManual from '../components/MinibarManual';

const STATUS = {
    bos:   { label: 'Boş',   bg: 'bg-white',         border: 'border-slate-300', text: 'text-slate-700',  fill: 'bg-white text-slate-700 border-slate-300' },
    dolu:  { label: 'Dolu',  bg: 'bg-emerald-100',   border: 'border-emerald-500', text: 'text-emerald-800', fill: 'bg-emerald-500 text-white border-emerald-700' },
    cikis: { label: 'Çıkış', bg: 'bg-orange-100',    border: 'border-orange-500',  text: 'text-orange-800',  fill: 'bg-orange-500 text-white border-orange-700' },
};
const DND_FILL = 'bg-red-600 text-white border-red-800';

export default function Minibar() {
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
    const [products, setProducts] = useState([]);
    const [rooms, setRooms] = useState([]);
    const [activeFloor, setActiveFloor] = useState(1);
    const [activeRoom, setActiveRoom] = useState(null); // room_no string
    const [roomSearch, setRoomSearch] = useState('');
    const [editQty, setEditQty] = useState({}); // {code: qty} aktif oda için lokal düzenleme
    const [editStatus, setEditStatus] = useState('bos');
    const [editDnd, setEditDnd] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const searchRef = useRef(null);

    useEffect(() => {
        loadDaily();
        // eslint-disable-next-line
    }, [selectedDate]);

    const loadDaily = async () => {
        setLoading(true);
        try {
            const res = await api.get(`/minibar/daily/${selectedDate}`);
            setProducts(res.data.products || []);
            setRooms(res.data.rooms || []);
            // Aktif oda hâlâ aynı odaysa onu güncelle
            if (activeRoom) {
                const r = (res.data.rooms || []).find(x => x.room_no === activeRoom);
                if (r) loadRoomToEditor(r, res.data.products || []);
            }
        } catch {
            toast.error('Minibar verileri yüklenemedi');
        } finally {
            setLoading(false);
        }
    };

    const loadRoomToEditor = (room, prods = products) => {
        const map = {};
        (room.items || []).forEach(it => { map[it.code] = it.qty || 0; });
        // Eksik ürün kodlarını 0 ile doldur
        (prods || []).forEach(p => { if (map[p.code] === undefined) map[p.code] = 0; });
        setEditQty(map);
        setEditStatus(room.status || 'bos');
        setEditDnd(!!room.dnd);
    };

    const selectRoom = (room_no) => {
        const r = rooms.find(x => x.room_no === room_no);
        if (!r) {
            toast.error(`Oda ${room_no} bulunamadı`);
            return;
        }
        setActiveRoom(room_no);
        setActiveFloor(r.floor);
        loadRoomToEditor(r);
    };

    const handleSearchSubmit = (e) => {
        e?.preventDefault?.();
        const q = (roomSearch || '').trim();
        if (!q) return;
        selectRoom(q);
    };

    const setQty = (code, val) => {
        if (editDnd) return; // DND'de ürün girilmez
        const v = val === '' ? 0 : Math.max(0, Number(val) || 0);
        setEditQty(p => ({ ...p, [code]: v }));
    };

    const toggleDnd = () => {
        const next = !editDnd;
        setEditDnd(next);
        if (next) {
            // DND açılınca ürünler sıfırlanır
            const reset = {};
            products.forEach(p => { reset[p.code] = 0; });
            setEditQty(reset);
        }
    };

    const saveRoom = async () => {
        if (!activeRoom) return;
        setSaving(true);
        try {
            const items = products.map(p => ({
                code: p.code,
                name: p.name,
                qty: editDnd ? 0 : Number(editQty[p.code] || 0),
                unit_price_tl: p.unit_price_tl || 0,
            }));
            await api.post('/minibar/save', {
                date: selectedDate,
                room_no: activeRoom,
                status: editStatus,
                dnd: editDnd,
                items,
            });
            toast.success(`Oda ${activeRoom} kaydedildi`);
            loadDaily();
        } catch {
            toast.error('Kaydetme başarısız');
        } finally {
            setSaving(false);
        }
    };

    const closeRoomEditor = () => {
        setActiveRoom(null);
        setEditQty({});
        setEditStatus('bos');
        setEditDnd(false);
    };

    const missingPriceProducts = products.filter(p => !p.has_price);

    const roomTotal = useMemo(() => {
        if (editDnd) return 0;
        return products.reduce((s, p) => s + (Number(editQty[p.code] || 0) * (p.unit_price_tl || 0)), 0);
    }, [editQty, products, editDnd]);

    const floorRooms = useMemo(() => rooms.filter(r => r.floor === activeFloor), [rooms, activeFloor]);

    return (
        <div className="space-y-4" data-testid="minibar-page">
            {/* Üst başlık + sekme barı */}
            <div className="flex items-center gap-2">
                <Coffee className="w-7 h-7 text-emerald-600" />
                <div>
                    <h1 className="font-heading text-2xl font-bold text-slate-800">Minibar</h1>
                    <p className="text-slate-500 text-sm">340 oda · 5 kat · dolum takibi ve raporlar</p>
                </div>
            </div>

            <Tabs defaultValue="dolum">
                <TabsList data-testid="minibar-tabs">
                    <TabsTrigger value="dolum" data-testid="tab-dolum">
                        <ClipboardList className="w-4 h-4 mr-1" /> Dolum
                    </TabsTrigger>
                    <TabsTrigger value="raporlar" data-testid="tab-raporlar">
                        <BarChart3 className="w-4 h-4 mr-1" /> Raporlar
                    </TabsTrigger>
                    <TabsTrigger value="manuel" data-testid="tab-manuel">
                        <CalendarRange className="w-4 h-4 mr-1" /> Manuel Giriş
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="dolum" className="space-y-4 mt-4">
            {/* Header: oda arama (sol), tarih (sağ) */}
            <div className="flex items-center justify-end flex-wrap gap-3">
                <div className="flex items-center gap-2">
                    <form onSubmit={handleSearchSubmit} className="flex items-center gap-1">
                        <Input
                            ref={searchRef}
                            placeholder="Oda no (örn. 213)"
                            value={roomSearch}
                            onChange={(e) => setRoomSearch(e.target.value)}
                            className="w-40 h-10"
                            data-testid="minibar-room-search"
                        />
                        <Button type="submit" size="icon" variant="outline" className="h-10 w-10" data-testid="minibar-room-search-btn">
                            <Search className="w-4 h-4" />
                        </Button>
                    </form>
                    <Input
                        type="date"
                        value={selectedDate}
                        onChange={(e) => setSelectedDate(e.target.value)}
                        className="w-44 h-10"
                        data-testid="minibar-date"
                    />
                </div>
            </div>

            {/* Fiyatsız uyarı */}
            {missingPriceProducts.length > 0 && (
                <div className="flex items-start gap-2 p-3 rounded-md bg-amber-50 border border-amber-300 text-amber-800 text-sm" data-testid="minibar-missing-price-warning">
                    <AlertTriangle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                    <div>
                        <b>{missingPriceProducts.length}</b> ürün fiyatsız: <b>{missingPriceProducts.map(p => p.name).join(', ')}</b>.
                        Lütfen <b>Ürünler</b> ekranından fiyatları girin.
                    </div>
                </div>
            )}

            {/* Aktif oda editörü */}
            {activeRoom && (
                <Card className="border-2 border-emerald-500" data-testid="active-room-editor">
                    <CardHeader className="pb-2 flex flex-row items-center justify-between">
                        <CardTitle className="text-lg flex items-center gap-2">
                            <span>Oda</span>
                            <Badge className="text-base bg-emerald-600 px-3 py-1">{activeRoom}</Badge>
                            <span className="text-xs text-slate-500">({activeFloor}. Kat)</span>
                            {editDnd && <Badge className="bg-red-600 text-white">DND</Badge>}
                        </CardTitle>
                        <Button variant="ghost" size="icon" onClick={closeRoomEditor} data-testid="close-room-editor">
                            <X className="w-5 h-5" />
                        </Button>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {/* Durum + DND butonları */}
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm text-slate-600 mr-2">Durum:</span>
                            {Object.entries(STATUS).map(([key, cfg]) => (
                                <Button
                                    key={key}
                                    type="button"
                                    variant={editStatus === key ? 'default' : 'outline'}
                                    onClick={() => setEditStatus(key)}
                                    className={editStatus === key ? `${cfg.bg} ${cfg.text} border-2 ${cfg.border} hover:opacity-90` : ''}
                                    data-testid={`status-${key}`}
                                >
                                    {cfg.label}
                                </Button>
                            ))}
                            <Button
                                type="button"
                                variant={editDnd ? 'default' : 'outline'}
                                onClick={toggleDnd}
                                className={editDnd ? 'bg-red-600 hover:bg-red-700 text-white' : ''}
                                data-testid="toggle-dnd"
                            >
                                <BellOff className="w-4 h-4 mr-1" /> DND
                            </Button>
                        </div>

                        {/* 9 ürün adet girişi */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                            {products.map(p => (
                                <div key={p.code} className={`flex items-center justify-between gap-3 p-2 border rounded-md ${editDnd ? 'opacity-40' : 'bg-slate-50'}`}>
                                    <div className="min-w-0 flex-1">
                                        <div className="text-[10px] text-slate-400 font-mono">#{p.code}</div>
                                        <div className="text-sm font-medium text-slate-800 truncate">{p.name}</div>
                                        <div className="text-xs text-slate-500">{formatTRNumber(p.unit_price_tl, 2)} ₺ / {p.unit}</div>
                                    </div>
                                    <Input
                                        type="number"
                                        min="0"
                                        value={editQty[p.code] ?? 0}
                                        onChange={(e) => setQty(p.code, e.target.value)}
                                        disabled={editDnd}
                                        className="w-20 h-9 text-center"
                                        data-testid={`qty-${p.code}`}
                                    />
                                </div>
                            ))}
                        </div>

                        {/* Toplam + Kaydet */}
                        <div className="flex items-center justify-between pt-2 border-t">
                            <div className="text-sm text-slate-600">
                                Oda Toplamı:{' '}
                                <span className="text-lg font-bold text-emerald-700" data-testid="room-total">
                                    {formatTRNumber(roomTotal, 2)} ₺
                                </span>
                            </div>
                            <Button onClick={saveRoom} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-room">
                                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                                Odayı Kaydet
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Kat sekmeleri */}
            <Card>
                <CardHeader className="pb-3">
                    <div className="flex items-center gap-2 flex-wrap">
                        {[1, 2, 3, 4, 5].map(f => (
                            <Button
                                key={f}
                                variant={activeFloor === f ? 'default' : 'outline'}
                                onClick={() => setActiveFloor(f)}
                                className={activeFloor === f ? 'bg-slate-800 hover:bg-slate-900' : ''}
                                data-testid={`floor-tab-${f}`}
                            >
                                {f}. Kat
                            </Button>
                        ))}
                        <div className="ml-auto text-xs text-slate-500 flex items-center gap-3">
                            <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-white border border-slate-300"></span> Boş</span>
                            <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-emerald-500"></span> Dolu</span>
                            <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-orange-500"></span> Çıkış</span>
                            <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-600"></span> DND</span>
                        </div>
                    </div>
                </CardHeader>
                <CardContent>
                    {loading ? (
                        <div className="text-center py-12 text-slate-400">
                            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2" />
                            Yükleniyor...
                        </div>
                    ) : (
                        <div className="grid grid-cols-6 sm:grid-cols-8 md:grid-cols-10 lg:grid-cols-12 gap-2" data-testid={`floor-grid-${activeFloor}`}>
                            {floorRooms.map(r => {
                                const cfg = STATUS[r.status] || STATUS.bos;
                                // DND baskın: hangi durum olursa olsun DND ise kırmızı dolgu
                                const cellClass = r.dnd ? DND_FILL : cfg.fill;
                                const isActive = activeRoom === r.room_no;
                                return (
                                    <button
                                        key={r.room_no}
                                        onClick={() => selectRoom(r.room_no)}
                                        className={`relative aspect-square rounded-md border-2 ${cellClass} font-semibold text-sm transition-all hover:scale-105 hover:shadow-md ${isActive ? 'ring-2 ring-offset-1 ring-blue-600' : ''}`}
                                        data-testid={`room-${r.room_no}`}
                                        title={`${r.room_no} — ${r.dnd ? 'DND' : cfg.label}`}
                                    >
                                        {r.room_no}
                                        {r.total_tl > 0 && !r.dnd && (
                                            <span className={`absolute bottom-0.5 left-0.5 right-0.5 text-xs font-bold truncate ${r.status === 'bos' ? 'text-slate-700' : 'text-white'}`}>
                                                {formatTRNumber(r.total_tl, 0)}₺
                                            </span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </CardContent>
            </Card>
                </TabsContent>

                <TabsContent value="raporlar" className="mt-4">
                    <MinibarReports />
                </TabsContent>

                <TabsContent value="manuel" className="mt-4">
                    <MinibarManual />
                </TabsContent>
            </Tabs>
        </div>
    );
}
